import type { ReactNode } from "react";
import { WorkflowTracker, type AppShellProps, type MilestonesProps } from "@wayflyer/flyui";

import { useSpaNavigate } from "../../hooks/use-spa-navigate";

import { useWorkflowTrackerNavigation } from "./use-workflow-tracker-navigation";
import { useOnboardingWorkflowSteps } from "./use-workflow-steps";
import { useOnboardingWorkflowState } from "./use-workflow-state";
import { buildMilestones, type SubstepStatus } from "./utils";
import { useWorkflowTrackerFooter } from "./use-workflow-tracker-footer";

export interface OnboardingWorkflowTrackerProps {
  customerDomain: string | null;
  countryOfIncorporation?: string;
  currentStepId: string;
  currentSubstepId?: string;
  currentSubstepStatus?: SubstepStatus;
  currentStepStatus?: SubstepStatus;
  currentStepDisabled?: boolean;
  hasBusinessCountryData?: boolean;
  hasRevenueData?: boolean;
  hasBusinessLocationData?: boolean;
  hasBusinessDetailsData?: boolean;
  secondaryLogo?: AppShellProps["secondaryLogo"];
  onBeforeLeaveBanking?: () => Promise<void>;
  children: ReactNode;
}

export function OnboardingWorkflowTracker({
  customerDomain,
  countryOfIncorporation,
  currentStepId,
  currentSubstepId,
  currentSubstepStatus,
  currentStepStatus,
  currentStepDisabled,
  hasBusinessCountryData = false,
  hasRevenueData = false,
  hasBusinessLocationData = false,
  hasBusinessDetailsData = false,
  secondaryLogo,
  onBeforeLeaveBanking,
  children,
}: OnboardingWorkflowTrackerProps) {
  const { navigate: navigateCompanyCreation } = useSpaNavigate("CompanyCreation");
  const { navigate: navigateFundingApp } = useSpaNavigate("FundingApp");

  const { completedStepIds, completedSubstepIds, stagesConfig } = useOnboardingWorkflowState({
    customerDomain,
    countryOfIncorporation,
    hasBusinessCountryData,
    hasRevenueData,
    hasBusinessLocationData,
    hasBusinessDetailsData,
  });

  const { handleNavigateToStep, isStateRequired } = useWorkflowTrackerNavigation({
    countryOfIncorporation,
    navigateCompanyCreation,
    navigateFundingApp,
    completedStepIds,
    completedSubstepIds,
    currentStepId,
    currentSubstepId,
    onBeforeLeaveBanking,
  });

  const steps = useOnboardingWorkflowSteps({
    includeBusinessLocation: isStateRequired,
    stagesConfig,
    onNavigateToStep: handleNavigateToStep,
  });

  const milestones = buildMilestones(
    steps,
    completedStepIds,
    completedSubstepIds,
    currentSubstepId,
    currentSubstepStatus,
    currentStepId,
    currentStepStatus,
    currentStepDisabled
  );
  const stepInfo: MilestonesProps = {
    steps: milestones,
    currentStepId,
    currentSubstepId,
  };

  const footer = useWorkflowTrackerFooter(currentSubstepId);

  return (
    <WorkflowTracker key={currentStepId} stepInfo={stepInfo} secondaryLogo={secondaryLogo} footer={footer}>
      {children}
    </WorkflowTracker>
  );
}
